import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { buildMeterIndexMap, readingKey } from "../utils/meterIndex";
import { useNavigate } from "react-router-dom";
// ContributionDashboard import removed - not needed since Overview/Compact tabs are removed
import api from "../utils/api";
import ForecastingChart from "../components/ForecastingChart";
import HierarchyTreeView from "../components/HierarchyTreeView";
import PowerDistributionFlow from "../components/PowerDistributionFlow";
import { useBranch } from "../context/BranchContext";
import { classifyError, shouldShowError } from "../utils/errorClassifier";
import { ErrorBanner, FullErrorState } from "../components/ErrorComponents";
import FieldHelp from "../help/FieldHelp";
import {
  FOCUS_RATED_POWER,
  buildNodeSettingsLink,
  needsRatedPowerAssignment,
} from "../utils/nodeSettingsLink";


// ========== TREE HELPERS ==========
const normalizeTree = (node) => {
  if (!node) return null;
  let childrenArray = [];
  if (Array.isArray(node.children)) {
    childrenArray = node.children.map(normalizeTree);
  } else if (typeof node.children === "object" && node.children !== null) {
    childrenArray = Object.values(node.children).map(normalizeTree);
  }
  return { ...node, children: childrenArray };
};

const isSourceCategory = (cat) =>
  ["GRID", "SOLAR", "GENERATOR"].includes((cat || "").toUpperCase());

const collectAllMachines = (node) => {
  const machines = [];
  const seenMeters = new Set();
  const walk = (n) => {
    const key = n.meter_id || n.machine_name;
    if (n.machine_name && key && !seenMeters.has(key)) {
      seenMeters.add(key);
      machines.push({
        name: n.machine_name,
        meter_id: n.meter_id || null,
        category: (n.category || "MACHINE").toUpperCase(),
        is_virtual: !!n.is_virtual,
        rated_power: n.rated_power,
        is_multi_parent: !!n.is_multi_parent || (Array.isArray(n.parent_meter_ids) && n.parent_meter_ids.length > 1),
        parent_meter_ids: n.parent_meter_ids || [],
      });
    }
    (n.children || []).forEach(walk);
  };
  if (node) walk(node);
  return machines;
};

const getMachineBranch = (machine, machineToBranchMap, meterIdToBranchMap) =>
  machineToBranchMap[machine.name] ||
  meterIdToBranchMap[String(machine.meter_id)] ||
  null;

const filterTreeByBranch = (node, selectedBranch, machineToBranchMap, meterIdToBranchMap) => {
  if (!node) return null;

  const branchName =
    machineToBranchMap[node.machine_name] ||
    meterIdToBranchMap[String(node.meter_id)] ||
    null;

  const filteredChildren = (node.children || [])
    .map((child) => filterTreeByBranch(child, selectedBranch, machineToBranchMap, meterIdToBranchMap))
    .filter(Boolean);

  const matchesBranch =
    node.machine_name === selectedBranch ||
    branchName === selectedBranch;

  if (matchesBranch || filteredChildren.length > 0) {
    return {
      ...node,
      children: filteredChildren,
    };
  }

  return null;
};

// Only Channel Checker view is needed - Overview and Compact tabs removed


// ========== Channel Checker Helper Functions ==========
const getStatus = (powerKw, _isStale, category) => {
  if (powerKw === null || powerKw === undefined) return "OFF";
  const threshold = category === "GENERATOR" ? 0.5 : 0.05;
  if (powerKw <= threshold) return "OFF";
  return "ON";
};

const getMeterStatus = (isStale, isVirtual) => {
  if (isVirtual) return "-";
  return isStale ? "offline" : "online";
};

// Health status tooltips (based on % of rated power)
const healthStatusTooltips = {
  Normal: "Machine is running fine. No issues.",
  Warning: "Machine is close to its rated power. Keep an eye on it.",
  Critical: "Machine has crossed its rated power. Needs attention now.",
};

// Badge component for Channel Checker
const StatusBadge = ({ value, type }) => {
  const getColor = () => {
    if (type === "status") {
      return value === "ON" ? "#90EE90" : "#FA8072";
    }
    if (type === "meterStatus") {
      if (value === "online") return "#90EE90";
      if (value === "idle") return "#FFD700";
      if (value === "offline") return "#FA8072";
      return "transparent";
    }
    if (type === "healthStatus") {
      if (value === "Normal") return "#90EE90";
      if (value === "Warning") return "#FFD700";
      if (value === "Critical") return "#FA8072";
      return "transparent";
    }
    return "transparent";
  };

  const getTooltip = () => {
    if (type === "healthStatus" && healthStatusTooltips[value]) {
      return healthStatusTooltips[value];
    }
    return null;
  };

  if (value === "-") {
    return <span>-</span>;
  }

  return (
    <span
      style={{
        backgroundColor: getColor(),
        borderRadius: "4px",
        padding: "2px 12px",
        display: "inline-block",
        fontWeight: 500,
        cursor: "default",
      }}
      title={getTooltip()}
    >
      {value}
    </span>
  );
};

// Color for a health status string
const healthStatusColor = (status) => {
  if (status === "Normal") return "#90EE90";
  if (status === "Warning") return "#FFD700";
  if (status === "Critical") return "#FA8072";
  return "transparent";
};

// Interactive Health Status Cell for Table
const HealthStatusCell = ({ row, onAssignRatedPower }) => {
  const [hovered, setHovered] = useState(false);

  // New backend shape: object { percent, status }. Show the percent, colored by status.
  const h = row.healthStatus;
  if (h && typeof h === "object" && h.status) {
    const label = `${Number(h.percent).toFixed(1)}%`;
    const isCritical = h.status === "Critical";
    return (
      <>
        {isCritical && (
          <style>{`@keyframes hsCriticalBlink{0%,100%{background-color:#ef4444;}50%{background-color:#7f1d1d;}}`}</style>
        )}
        <span
          style={{
            backgroundColor: healthStatusColor(h.status),
            borderRadius: "4px",
            padding: "2px 12px",
            display: "inline-block",
            fontWeight: 500,
            cursor: "default",
            ...(isCritical
              ? { animation: "hsCriticalBlink 1s ease-in-out infinite", color: "#fff" }
              : {}),
          }}
          title={healthStatusTooltips[h.status] || h.status}
        >
          {label}
        </span>
      </>
    );
  }

  // No health figure. Two different reasons land here and they need different
  // treatment: the machine has no rated power (the user can fix that, so offer
  // the link), or it has one and is simply OFF right now (nothing to fix).
  const needsRatedPower = needsRatedPowerAssignment({
    isVirtual: row.isVirtual,
    isPhaseRow: row.isPhaseRow,
    ratedPower: row.ratedPower,
  });

  if (needsRatedPower) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onAssignRatedPower?.(row);
        }}
        title="Health status needs a rated power for this machine. Opens Node Settings."
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: "#fffbeb",
          border: "1px dashed #f59e0b",
          borderRadius: 4,
          padding: "2px 8px",
          color: "#b45309",
          fontSize: 11,
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        Assign rated power
      </button>
    );
  }

  // Rated power is set but there is no reading (OFF / stale): subtle dash + hint.
  return (
    <div
      style={{
        position: "relative",
        display: "inline-block",
        width: "100%",
        minHeight: "20px",
        cursor: "default",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span style={{ color: "#cbd5e1", fontWeight: 500 }}>—</span>

      {hovered && (
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            right: "0px",
            backgroundColor: "#1e293b",
            border: "1px solid #334155",
            borderRadius: "6px",
            padding: "6px 10px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 999,
            width: "220px",
            textAlign: "center",
            marginBottom: "6px",
            color: "#cbd5e1",
            fontSize: "11px",
            lineHeight: "1.4",
            pointerEvents: "none",
            whiteSpace: "normal",
          }}
        >
          No health status while the machine is off — it is calculated from live
          power against the rated power.
        </div>
      )}
    </div>
  );
};

// Display value for table cells; virtual grouping rows omit placeholder dashes
const formatTableCell = (value, isVirtual) => {
  if (value === "-" || value == null || value === "") {
    return isVirtual ? "" : "-";
  }
  return value;
};

/* Current on a 3-phase meter is reported as i1 + i2 + i3 (live_raw_calculator.py),
   i.e. a sum. "Average" spreads that across the three phases so the figure is
   comparable with a single-phase machine. Meters that report one phase have no sum to
   spread, so both modes show the same number. */
const formatCurrent = (reading, mode) => {
  if (!reading) return "-";
  const total = reading.current;
  if (total == null || total === "" || !Number.isFinite(Number(total))) return "-";

  const numTotal = Number(total);
  if (mode === "average") {
    if (reading.phase_type === 1) {
      return numTotal.toFixed(1);
    }
    if (reading.phases) {
      const perPhase = ["phase1", "phase2", "phase3"]
        .map((key) => {
          const val = reading.phases[key]?.current;
          return val != null && Number.isFinite(Number(val)) ? Number(val) : null;
        })
        .filter((val) => val !== null);
      if (perPhase.length > 0) {
        return (perPhase.reduce((sum, v) => sum + v, 0) / perPhase.length).toFixed(1);
      }
    }
    return (numTotal / 3).toFixed(1);
  }
  return numTotal.toFixed(1);
};

const getVirtualCategoryLabel = (category) => {
  const cat = (category || "").trim().toUpperCase();
  return cat || "VIRTUAL";
};

const VirtualCategoryLabel = ({ category }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.03em",
      textTransform: "uppercase",
      color: "#0f766e",
      backgroundColor: "#d1fae5",
      border: "1px solid #99f6e4",
      borderRadius: 4,
      padding: "2px 6px",
      flexShrink: 0,
    }}
  >
    <FieldHelp id="live.nodeCategory">{getVirtualCategoryLabel(category)}</FieldHelp>
  </span>
);

// ========== Table Column Definitions & Persistence ==========
const COLUMN_STORAGE_KEY_PREFIX = "enviz_live_table_cols_";

const DEFAULT_TABLE_COLUMNS = {
  timestamp: true,
  power: true,
  current: true,
  voltage: true,
  powerFactor: true,
  frequency: false,
  thdVoltage: false,
  thdCurrent: false,
  tap: false,
  status: false,
  meterStatus: true,
  healthStatus: true,
};

const COLUMN_DEFINITIONS = [
  { key: "timestamp", label: "Time", isDefault: true, helpId: "live.lastUpdated" },
  { key: "power", label: "Power (kW)", isDefault: true, sortable: true, helpId: "live.power" },
  { key: "current", label: "Avg Current (A)", isDefault: true, sortable: true, helpId: "live.current" },
  { key: "voltage", label: "Voltage (V)", isDefault: true, sortable: true, helpId: "live.voltage" },
  { key: "powerFactor", label: "Power Factor", isDefault: true, sortable: true, helpId: "live.powerFactor" },
  { key: "frequency", label: "Frequency (Hz)", isDefault: false, sortable: true, helpId: "live.frequency" },
  { key: "thdVoltage", label: "THD V (%)", isDefault: false, sortable: true, helpId: "live.thdVoltage" },
  { key: "thdCurrent", label: "THD I (%)", isDefault: false, sortable: true, helpId: "live.thdCurrent" },
  { key: "tap", label: "TAP (kW)", isDefault: false, sortable: true, helpId: "live.tap" },
  { key: "status", label: "Status (On/Off)", isDefault: false, sortable: false, helpId: "live.onOffStatus" },
  { key: "meterStatus", label: "Meter Status", isDefault: true, sortable: false, helpId: "live.meterStatus" },
  { key: "healthStatus", label: "Health Status", isDefault: true, sortable: false, helpId: "live.healthStatus" },
];

// Interactive Average Current Cell with 3-Phase Breakdown Tooltip
const AvgCurrentCell = ({ row }) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const cellRef = useRef(null);

  const phases = row?.phases;
  const phaseType = row?.phase_type;
  const hasPhases = phases && (phases.phase1 || phases.phase2 || phases.phase3);
  const is3Phase = phaseType === 3 || Boolean(hasPhases);
  const val = row?.current;

  if (!val || val === "-") return <span>-</span>;

  // Single-phase meter: simple display
  if (!is3Phase) {
    return (
      <span style={{ fontVariantNumeric: "tabular-nums" }} title="Single-phase current">
        {val}
      </span>
    );
  }

  const p1 = phases?.phase1?.current != null ? Number(phases.phase1.current).toFixed(1) : "-";
  const p2 = phases?.phase2?.current != null ? Number(phases.phase2.current).toFixed(1) : "-";
  const p3 = phases?.phase3?.current != null ? Number(phases.phase3.current).toFixed(1) : "-";

  const showTooltip = () => {
    if (cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      // If space above is less than 170px, show BELOW the cell to avoid table header occlusion
      const placeBelow = rect.top < 170;
      setCoords({
        left: rect.left + rect.width / 2,
        top: placeBelow ? rect.bottom + 6 : rect.top - 6,
        placeBelow,
      });
    }
    setOpen(true);
  };

  const hideTooltip = () => {
    setOpen(false);
  };

  return (
    <div
      ref={cellRef}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        cursor: "pointer",
      }}
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onClick={() => {
        if (!open) showTooltip();
        else hideTooltip();
      }}
      title="3-Phase current: hover or click to inspect L1, L2, L3 phases"
    >
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          fontWeight: 600,
          borderBottom: "1.5px dashed #0284c7",
          color: "#0369a1",
        }}
      >
        {val}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 800,
          color: "#0284c7",
          backgroundColor: "#e0f2fe",
          border: "1px solid #bae6fd",
          borderRadius: 3,
          padding: "0 3px",
          lineHeight: "13px",
        }}
      >
        3φ
      </span>

      {open && coords && createPortal(
        <div
          style={{
            position: "fixed",
            top: coords.top,
            left: coords.left,
            transform: coords.placeBelow ? "translateX(-50%)" : "translate(-50%, -100%)",
            backgroundColor: "#1e293b",
            color: "#fff",
            borderRadius: 8,
            padding: "8px 12px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.2)",
            zIndex: 999999,
            minWidth: 160,
            textAlign: "left",
            fontSize: 11,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: "#94a3b8",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: 4,
              borderBottom: "1px solid #334155",
              paddingBottom: 3,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <span>Phase Currents</span>
            <span style={{ color: "#38bdf8" }}>3-Phase</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "2px 0" }}>
            <span style={{ color: "#ef4444", fontWeight: 700 }}>● L1 (Phase 1):</span>
            <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{p1} A</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "2px 0" }}>
            <span style={{ color: "#eab308", fontWeight: 700 }}>● L2 (Phase 2):</span>
            <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{p2} A</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "2px 0" }}>
            <span style={{ color: "#3b82f6", fontWeight: 700 }}>● L3 (Phase 3):</span>
            <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{p3} A</span>
          </div>
          <div
            style={{
              marginTop: 4,
              paddingTop: 4,
              borderTop: "1px solid #334155",
              display: "flex",
              justifyContent: "space-between",
              color: "#94a3b8",
            }}
          >
            <span>Average:</span>
            <span style={{ color: "#fff", fontWeight: 700 }}>{val} A</span>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ========== Unified Searchable Multi-Select Equipment Component ==========
const SearchableEquipmentSelect = ({
  selectedLoads,
  searchQuery,
  onSelectLoads,
  onSearchQuery,
  options = [],
}) => {
  const [open, setOpen] = useState(false);
  const [typedSearch, setTypedSearch] = useState("");
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close popover when clicking outside or pressing Escape
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [open]);

  // Filter options based on typed search query
  const filteredOptions = useMemo(() => {
    const q = typedSearch.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => {
      const name = (opt.name || "").toLowerCase();
      const mid = opt.meter_id ? String(opt.meter_id).toLowerCase() : "";
      const cat = (opt.category || "").toLowerCase();
      return name.includes(q) || mid.includes(q) || cat.includes(q);
    });
  }, [options, typedSearch]);

  const isAllSelected =
    selectedLoads === "all" ||
    (Array.isArray(selectedLoads) &&
      (selectedLoads.includes("all") || (options.length > 0 && selectedLoads.length === options.length)));

  const isMachineSelected = (name) => {
    if (isAllSelected) return true;
    if (Array.isArray(selectedLoads)) return selectedLoads.includes(name);
    return selectedLoads === name;
  };

  const handleToggleItem = (name) => {
    onSearchQuery("");
    if (isAllSelected) {
      // Transition from "all" to all EXCEPT this one
      const remaining = options.map((o) => o.name).filter((n) => n !== name);
      onSelectLoads(remaining);
    } else if (Array.isArray(selectedLoads)) {
      if (selectedLoads.includes(name)) {
        const next = selectedLoads.filter((n) => n !== name);
        onSelectLoads(next.length === 0 ? [] : next);
      } else {
        const next = [...selectedLoads, name];
        if (next.length === options.length) {
          onSelectLoads("all");
        } else {
          onSelectLoads(next);
        }
      }
    } else {
      if (selectedLoads === name) {
        onSelectLoads([]);
      } else {
        onSelectLoads([selectedLoads, name]);
      }
    }
  };

  const handleSelectOnly = (name, e) => {
    e.stopPropagation();
    onSelectLoads([name]);
    onSearchQuery("");
    setTypedSearch("");
  };

  const handleSelectAll = () => {
    onSelectLoads("all");
    onSearchQuery("");
    setTypedSearch("");
  };

  const handleDeselectAll = () => {
    onSelectLoads([]);
    onSearchQuery("");
    setTypedSearch("");
  };

  const handleApplyFilterSearch = (q) => {
    onSearchQuery(q);
    setOpen(false);
  };

  const selectedCount = isAllSelected
    ? options.length
    : Array.isArray(selectedLoads)
      ? selectedLoads.length
      : selectedLoads && selectedLoads !== "all"
        ? 1
        : 0;

  let displayText = "All Equipment";
  if (searchQuery) {
    displayText = `Filter: "${searchQuery}"`;
  } else if (isAllSelected) {
    displayText = `All Equipment (${options.length})`;
  } else if (Array.isArray(selectedLoads)) {
    if (selectedLoads.length === 0) {
      displayText = "None Selected (0)";
    } else if (selectedLoads.length === 1) {
      displayText = selectedLoads[0];
    } else {
      displayText = `${selectedLoads.length} Equipment Selected`;
    }
  } else if (selectedLoads && selectedLoads !== "all") {
    displayText = selectedLoads;
  }

  const hasFilter = !isAllSelected || Boolean(searchQuery);

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth: 230, flex: "0 1 270px" }}>
      {/* Trigger Button */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "7px 12px",
          borderRadius: 8,
          border: open ? "1.5px solid #0f766e" : "1px solid #cbd5e1",
          backgroundColor: "#ffffff",
          boxShadow: open ? "0 0 0 3px rgba(15, 118, 110, 0.15)" : "0 1px 2px rgba(0,0,0,0.04)",
          fontSize: 13,
          color: "#1e293b",
          cursor: "pointer",
          userSelect: "none",
          transition: "all 0.15s ease",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <span style={{ fontWeight: hasFilter ? 600 : 500, color: hasFilter ? "#0f172a" : "#475569" }}>
            {displayText}
          </span>
          {Array.isArray(selectedLoads) && selectedLoads.length > 1 && !isAllSelected && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 6px",
                borderRadius: 999,
                backgroundColor: "#ccfbf1",
                color: "#0f766e",
              }}
            >
              {selectedLoads.length}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {hasFilter && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleSelectAll();
              }}
              title="Reset to All Equipment"
              style={{
                background: "none",
                border: "none",
                color: "#94a3b8",
                fontSize: 12,
                cursor: "pointer",
                padding: "2px 4px",
                display: "flex",
                alignItems: "center",
              }}
            >
              ✕
            </button>
          )}
          <span style={{ fontSize: 10, color: "#94a3b8" }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {/* Popover Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            minWidth: 280,
            backgroundColor: "#ffffff",
            borderRadius: 10,
            border: "1px solid #e2e8f0",
            boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            zIndex: 100,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Integrated Search Input */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", backgroundColor: "#f8fafc" }}>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <input
                ref={searchInputRef}
                type="text"
                value={typedSearch}
                onChange={(e) => setTypedSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && typedSearch.trim()) {
                    handleApplyFilterSearch(typedSearch.trim());
                  }
                }}
                placeholder="Search equipment or meter..."
                style={{
                  width: "100%",
                  padding: "6px 26px 6px 8px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  fontSize: 12.5,
                  outline: "none",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                }}
              />
              {typedSearch && (
                <button
                  type="button"
                  onClick={() => setTypedSearch("")}
                  style={{
                    position: "absolute",
                    right: 6,
                    background: "none",
                    border: "none",
                    color: "#94a3b8",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Quick Action Toolbar: Select All / Deselect All */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "6px 12px",
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
              fontSize: 11.5,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                type="button"
                onClick={handleSelectAll}
                style={{
                  background: isAllSelected ? "#ccfbf1" : "#ffffff",
                  border: `1px solid ${isAllSelected ? "#0d9488" : "#cbd5e1"}`,
                  color: isAllSelected ? "#0f766e" : "#334155",
                  fontWeight: 700,
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 5,
                  cursor: "pointer",
                }}
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleDeselectAll}
                style={{
                  background: "#ffffff",
                  border: "1px solid #cbd5e1",
                  color: "#64748b",
                  fontWeight: 600,
                  fontSize: 11,
                  padding: "2px 8px",
                  borderRadius: 5,
                  cursor: "pointer",
                }}
              >
                Deselect All
              </button>
            </div>
            <span style={{ color: "#64748b", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
              {selectedCount} / {options.length}
            </span>
          </div>

          {/* Options List with Checkboxes */}
          <div style={{ maxHeight: 240, overflowY: "auto", padding: "4px 0" }}>
            {/* Filter by typed search option (if typed) */}
            {typedSearch.trim() && (
              <div
                onClick={() => handleApplyFilterSearch(typedSearch.trim())}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "7px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0369a1",
                  backgroundColor: "#f0f9ff",
                  borderBottom: "1px solid #e0f2fe",
                  cursor: "pointer",
                }}
              >
                <span>🔍 Filter table by &quot;{typedSearch.trim()}&quot;</span>
                <span style={{ fontSize: 10, color: "#0284c7" }}>↵ Enter</span>
              </div>
            )}

            {/* Equipment List */}
            {filteredOptions.length === 0 ? (
              <div style={{ padding: "12px", textAlign: "center", fontSize: 12, color: "#94a3b8" }}>
                No equipment matching &quot;{typedSearch}&quot;
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const checked = isMachineSelected(opt.name);
                return (
                  <div
                    key={opt.name}
                    onClick={() => handleToggleItem(opt.name)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "6px 12px",
                      fontSize: 12.5,
                      color: checked ? "#0f172a" : "#64748b",
                      backgroundColor: checked ? "#f0fdfa" : "transparent",
                      cursor: "pointer",
                      transition: "background-color 0.1s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!checked) e.currentTarget.style.backgroundColor = "#f8fafc";
                    }}
                    onMouseLeave={(e) => {
                      if (!checked) e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                      {/* Custom Checkbox */}
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 4,
                          border: checked ? "1.5px solid #0f766e" : "1.5px solid #94a3b8",
                          backgroundColor: checked ? "#0f766e" : "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "all 0.12s ease",
                        }}
                      >
                        {checked && (
                          <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
                            <path d="M1 5L4.5 8.5L11 1.5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </div>
                      <span style={{ fontWeight: checked ? 600 : 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {opt.name}
                      </span>
                      {opt.category && (
                        <span
                          style={{
                            fontSize: 9,
                            fontWeight: 700,
                            padding: "1px 4px",
                            borderRadius: 3,
                            backgroundColor: "#f1f5f9",
                            color: "#64748b",
                            textTransform: "uppercase",
                            flexShrink: 0,
                          }}
                        >
                          {opt.category}
                        </span>
                      )}
                    </div>

                    {/* Quick "Only" Action */}
                    <button
                      type="button"
                      onClick={(e) => handleSelectOnly(opt.name, e)}
                      title={`Select only ${opt.name}`}
                      style={{
                        background: "none",
                        border: "1px solid #cbd5e1",
                        borderRadius: 4,
                        padding: "1px 6px",
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#0f766e",
                        cursor: "pointer",
                        marginLeft: 8,
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#e0f2fe";
                        e.currentTarget.style.borderColor = "#0284c7";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = "#cbd5e1";
                      }}
                    >
                      Only
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== Channel Checker Component with Hierarchy & Real-time Search ==========
const ChannelCheckerContent = ({
  hierarchyTree,
  latestData,
  healthStatusData,
  loading,
  error,
  username,
  unitPrice,
  onRefresh,
  onAssignRatedPower,
}) => {
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const DEFAULT_VIEW_KEY = `enviz-default-machines-${username || "default"}`;
  const [selectedLoads, setSelectedLoads] = useState(() => {
    try {
      const stored = localStorage.getItem(`enviz-default-machines-${username || "default"}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed === "all" || (Array.isArray(parsed) && parsed.length > 0)) {
          return parsed;
        }
      }
    } catch {}
    return "all";
  });
  const [defaultSavedSuccess, setDefaultSavedSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState(null); // null = unsorted (hierarchy)
  const [sortDirection, setSortDirection] = useState("desc");
  const [gearOpen, setGearOpen] = useState(false);

  const handleSaveDefaultView = () => {
    try {
      const toStore = isAllSelected ? "all" : selectedLoads;
      localStorage.setItem(DEFAULT_VIEW_KEY, JSON.stringify(toStore));
      setDefaultSavedSuccess(true);
      setTimeout(() => setDefaultSavedSuccess(false), 2400);
    } catch (e) {
      console.error("Failed to save default equipment view", e);
    }
  };

  const handleResetDefaultView = () => {
    try {
      localStorage.removeItem(DEFAULT_VIEW_KEY);
      setSelectedLoads("all");
      setDefaultSavedSuccess(true);
      setTimeout(() => setDefaultSavedSuccess(false), 2400);
    } catch (e) {}
  };

  // Load custom columns from localStorage or default
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const stored = localStorage.getItem(COLUMN_STORAGE_KEY_PREFIX + (username || "default"));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed === "object") {
          return { ...DEFAULT_TABLE_COLUMNS, ...parsed };
        }
      }
    } catch {}
    return { ...DEFAULT_TABLE_COLUMNS };
  });

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem(COLUMN_STORAGE_KEY_PREFIX + (username || "default"), JSON.stringify(next));
      } catch {}
      return next;
    });
  };

  const resetColumnsToDefault = () => {
    setVisibleColumns({ ...DEFAULT_TABLE_COLUMNS });
    try {
      localStorage.removeItem(COLUMN_STORAGE_KEY_PREFIX + (username || "default"));
    } catch {}
  };

  // Index for machines sharing one physical meter. See utils/meterIndex.js
  const meterIndexMap = useMemo(
    () => buildMeterIndexMap(hierarchyTree),
    [hierarchyTree],
  );

  // Create readings map keyed by meter_id (or meter_id_index for shared meters)
  const latestReadingsMap = useMemo(() => {
    if (!latestData?.readings) return new Map();
    const map = new Map();
    latestData.readings.forEach((r) => {
      const mid = r.meter_id;
      if (!mid) return;
      const key = r.meter_index != null ? `${mid}_${r.meter_index}` : mid;
      map.set(key, r);
    });
    return map;
  }, [latestData]);

  // Get list of all available loads/machines for dropdown & search
  const availableLoads = useMemo(() => {
    if (hierarchyTree) {
      return collectAllMachines(hierarchyTree);
    }
    if (latestData?.readings) {
      return latestData.readings.map((r) => ({
        name: r.machine_name || r.appliance_name,
        meter_id: r.meter_id || null,
        category: "MACHINE",
        is_virtual: false,
      }));
    }
    return [];
  }, [hierarchyTree, latestData]);

  // Helper to get reading data for a machine
  const getReadingData = useCallback(
    (machineName, category, meterId) => {
      const key = readingKey(meterId, meterIndexMap, machineName);
      const reading = key ? latestReadingsMap.get(key) : null;
      const loadInfo = availableLoads.find((l) => l.name === machineName);
      const rp = reading?.rated_power !== undefined ? reading.rated_power : (loadInfo?.rated_power ?? null);
      const ratedPower = (rp !== null && rp !== undefined) ? Number(rp) : null;

      if (!reading) {
        return {
          timestamp: "-",
          power: "-",
          current: "-",
          voltage: "-",
          powerFactor: "-",
          frequency: "-",
          thdVoltage: "-",
          thdCurrent: "-",
          tap: "-",
          status: "-",
          meterStatus: "-",
          healthStatus: "-",
          ratedPower,
          meterId: meterId || null,
          phase_type: null,
          phases: null,
        };
      }
      // Virtual nodes only have calculated power — no physical meter readings
      if (reading.is_virtual) {
        return {
          timestamp: "-",
          power: reading.power_kw?.toFixed(2) ?? "-",
          current: "-",
          voltage: "-",
          powerFactor: "-",
          frequency: "-",
          thdVoltage: "-",
          thdCurrent: "-",
          tap: "-",
          status: "-",
          meterStatus: "-",
          healthStatus: "-",
          phase_type: null,
          phases: null,
          ratedPower,
          meterId: meterId || null,
        };
      }
      const isStale = !!reading.is_stale;
      let hs = healthStatusData?.[key] || "-";
      if (
        (hs === "-" || !hs || typeof hs !== "object" || !hs.status) &&
        ratedPower &&
        ratedPower > 0 &&
        reading.power_kw != null &&
        !isStale
      ) {
        const pct = (Math.abs(reading.power_kw) / ratedPower) * 100;
        if (pct > 100) hs = { status: "Critical", percent: pct };
        else if (pct >= 80) hs = { status: "Warning", percent: pct };
        else hs = { status: "Normal", percent: pct };
      } else if (
        hs &&
        typeof hs === "object" &&
        ratedPower &&
        ratedPower > 0 &&
        reading.power_kw != null &&
        Math.abs(reading.power_kw) > ratedPower
      ) {
        hs = { ...hs, status: "Critical", percent: (Math.abs(reading.power_kw) / ratedPower) * 100 };
      }

      return {
        timestamp: isStale ? "-" : reading.timestamp,
        power: isStale ? "-" : (reading.power_kw?.toFixed(2) ?? "-"),
        current: isStale ? "-" : formatCurrent(reading, "average"),
        voltage: isStale ? "-" : (reading.voltage?.toFixed(2) ?? "-"),
        powerFactor: isStale ? "-" : (reading.power_factor?.toFixed(2) ?? "-"),
        frequency: isStale ? "-" : (reading.frequency?.toFixed(2) ?? "-"),
        thdVoltage: isStale ? "-" : (reading.thd_voltage?.toFixed(2) ?? "-"),
        thdCurrent: isStale ? "-" : (reading.thd_current?.toFixed(2) ?? "-"),
        tap: isStale ? "-" : (reading.tap?.toFixed(2) ?? "-"),
        status: getStatus(isStale ? 0 : reading.power_kw, isStale, category),
        meterStatus: getMeterStatus(isStale, reading.is_virtual),
        healthStatus: hs,
        phase_type: reading.phase_type || null,
        phases: isStale ? null : reading.phases,
        ratedPower,
        meterId: meterId || null,
      };
    },
    [latestReadingsMap, healthStatusData, meterIndexMap, availableLoads],
  );

  // Build hierarchical rows by walking the tree recursively
  const hierarchicalData = useMemo(() => {
    if (!hierarchyTree) {
      if (!latestData?.readings) return [];
      return latestData.readings.map((reading) => {
        const machineName =
          reading.machine_name || reading.appliance_name || "Unknown";
        return {
          id: `node-${machineName}`,
          name: machineName,
          level: 0,
          hasChildren: false,
          isVirtual: !!reading.is_virtual,
          ...getReadingData(machineName, undefined, reading.meter_id),
        };
      });
    }

    const rows = [];
    const walk = (node, depth, parentMeterId = null) => {
      const name = node.machine_name || "Unknown";
      const mid = node.meter_id || name;
      const id = `node-${mid}-${depth}-${parentMeterId || 'root'}`;
      const children = node.children || [];
      const hasChildren = children.length > 0;
      const cat = (node.category || "").toUpperCase();
      let reading = getReadingData(name, cat, node.meter_id);

      const isSecondaryBranch =
        Boolean(node.is_multi_parent) &&
        Boolean(node.primary_parent_meter_id) &&
        Boolean(parentMeterId) &&
        node.primary_parent_meter_id !== parentMeterId;

      // Virtual node with no API reading — calculate power from children
      if (node.is_virtual && reading.power === "-" && hasChildren) {
        let calcPower = 0;
        const sumChildren = (n) => {
          const rKey = readingKey(n.meter_id, meterIndexMap, n.machine_name);
          const r = rKey ? latestReadingsMap.get(rKey) : null;
          if (r) {
            const pw = parseFloat(r.power_kw) || 0;
            const c = (n.category || "").toUpperCase();
            calcPower += c === "SOLAR" || c === "GENERATOR" ? Math.abs(pw) : pw;
          } else {
            (n.children || []).forEach(sumChildren);
          }
        };
        children.forEach(sumChildren);
        reading = { ...reading, power: calcPower.toFixed(2) };
      }

      rows.push({
        id,
        name,
        meterId: node.meter_id,
        level: depth,
        hasChildren,
        ...reading,
        isSource: isSourceCategory(cat),
        isVirtual: !!node.is_virtual,
        isMultiParent: !!node.is_multi_parent || (Array.isArray(node.parent_meter_ids) && node.parent_meter_ids.length > 1),
        isSecondaryBranch,
        parentMeterIds: node.parent_meter_ids || [],
        primaryParentMeterId: node.primary_parent_meter_id || null,
        category: cat,
      });

      if (hasChildren && expandedNodes.has(id)) {
        children.forEach((child) => walk(child, depth + 1, node.meter_id));
      }
    };

    walk(hierarchyTree, 0, null);
    return rows;
  }, [hierarchyTree, latestData, expandedNodes, getReadingData, latestReadingsMap, meterIndexMap]);

  // Flat data view - all machines without hierarchy
  const flatData = useMemo(() => {
    return availableLoads.map((load) => {
      const readingData = getReadingData(load.name, undefined, load.meter_id);
      return {
        id: `flat-${load.name}-${load.meter_id || ""}`,
        name: load.name,
        meterId: load.meter_id,
        level: 0,
        hasChildren: false,
        isVirtual: !!load.is_virtual,
        category: load.category,
        isMultiParent: !!load.is_multi_parent || (Array.isArray(load.parent_meter_ids) && load.parent_meter_ids.length > 1),
        ...readingData,
      };
    });
  }, [availableLoads, getReadingData]);

  const isAllSelected =
    selectedLoads === "all" ||
    (Array.isArray(selectedLoads) &&
      (selectedLoads.includes("all") || (availableLoads.length > 0 && selectedLoads.length === availableLoads.length)));

  // Sorting state determines whether table is flat (sorted) or hierarchy (unsorted)
  const isSorted = Boolean(sortColumn);
  const isFiltered = !isAllSelected || searchQuery.trim().length > 0;
  const viewMode = isSorted || isFiltered ? "flat" : "hierarchy";

  // Filter data based on selected load(s) and search query
  const filteredData = useMemo(() => {
    const baseList = viewMode === "hierarchy" ? hierarchicalData : flatData;
    let result = baseList;

    if (!isAllSelected) {
      const selectedSet = new Set(Array.isArray(selectedLoads) ? selectedLoads : [selectedLoads]);
      result = result.filter((item) => selectedSet.has(item.name));
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((item) => {
        const nameMatch = (item.name || "").toLowerCase().includes(q);
        const meterMatch = item.meterId ? String(item.meterId).toLowerCase().includes(q) : false;
        const catMatch = (item.category || "").toLowerCase().includes(q);
        return nameMatch || meterMatch || catMatch;
      });
    }

    return result;
  }, [hierarchicalData, flatData, selectedLoads, isAllSelected, searchQuery, viewMode]);

  // Sort data when sortColumn is selected
  const sortedData = useMemo(() => {
    if (!filteredData.length) return [];
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      if (sortColumn === "name") {
        const aVal = (a.name || "").toLowerCase();
        const bVal = (b.name || "").toLowerCase();
        return sortDirection === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      const numericColumns = [
        "power",
        "current",
        "voltage",
        "powerFactor",
        "frequency",
        "thdVoltage",
        "thdCurrent",
        "tap",
      ];
      if (numericColumns.includes(sortColumn)) {
        const aVal = parseFloat(a[sortColumn]) || 0;
        const bVal = parseFloat(b[sortColumn]) || 0;
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aVal = (a[sortColumn] || "").toString().toLowerCase();
      const bVal = (b[sortColumn] || "").toString().toLowerCase();
      return sortDirection === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Cycling sort: 1st click = desc (flat), 2nd click = asc, 3rd click = null (hierarchy)
  const handleSort = (column) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection("desc");
    } else if (sortDirection === "desc") {
      setSortDirection("asc");
    } else {
      setSortColumn(null);
      setSortDirection("desc");
    }
  };

  const SortHeader = ({ column, children, align }) => {
    const isThisSorted = sortColumn === column;
    return (
      <th
        style={{
          ...channelStyles.th,
          cursor: "pointer",
          userSelect: "none",
          textAlign: align || "center",
          backgroundColor: isThisSorted ? "#e0f2fe" : "#f8f9fa",
          color: isThisSorted ? "#0369a1" : "inherit",
          transition: "background-color 0.15s ease",
        }}
        onClick={() => handleSort(column)}
        title={
          isThisSorted
            ? sortDirection === "asc"
              ? "Click to desort (returns to hierarchy view)"
              : "Click to sort ascending"
            : "Click to sort (flattens table)"
        }
      >
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: align === "left" ? "flex-start" : "center", gap: 4 }}>
          {children}
          <span style={{ fontSize: 11, opacity: isThisSorted ? 1 : 0.35 }}>
            {isThisSorted ? (sortDirection === "asc" ? "▲" : "▼") : "⇅"}
          </span>
        </div>
      </th>
    );
  };

  const prevRootNameRef = useRef(null);

  // Expand only root node by default in hierarchy mode
  useEffect(() => {
    if (hierarchyTree && prevRootNameRef.current !== hierarchyTree.machine_name) {
      prevRootNameRef.current = hierarchyTree.machine_name;
      const mid = hierarchyTree.meter_id || hierarchyTree.machine_name || "Unknown";
      const rootId = `node-${mid}-0-root`;
      setExpandedNodes(new Set([rootId]));
    }
  }, [hierarchyTree]);

  const toggleNode = (nodeId) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const renderHierarchicalTable = () => {
    if (loading) {
      return <div style={channelStyles.loading}>Loading...</div>;
    }

    if (error && sortedData.length === 0) {
      return <div style={channelStyles.error}>{error}</div>;
    }

    return (
      <div style={channelStyles.tableContainer}>
        {/* Controls Row: Search Filter & Gear Column Customizer */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          {/* Search Inputs: Unified Searchable Equipment Select */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", flex: 1 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
              Equipment:
            </label>

            <SearchableEquipmentSelect
              selectedLoads={selectedLoads}
              searchQuery={searchQuery}
              onSelectLoads={setSelectedLoads}
              onSearchQuery={setSearchQuery}
              options={availableLoads}
            />

            {/* Set as Default View Button with instant feedback */}
            <button
              type="button"
              onClick={handleSaveDefaultView}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 12px",
                borderRadius: 8,
                border: defaultSavedSuccess ? "1.5px solid #10b981" : "1px solid #0f766e",
                backgroundColor: defaultSavedSuccess ? "#ecfdf5" : "#ffffff",
                color: defaultSavedSuccess ? "#059669" : "#0f766e",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                userSelect: "none",
              }}
              title={`Save current equipment selection as the default view for ${username || "this client"}`}
            >
              {defaultSavedSuccess && <span style={{ fontSize: 12 }}>✓</span>}
              <span>{defaultSavedSuccess ? "Default Saved!" : "Set Default View"}</span>
            </button>

            {/* Reset Default View if custom default exists */}
            {(() => {
              try {
                const stored = localStorage.getItem(DEFAULT_VIEW_KEY);
                if (stored && stored !== `"all"`) {
                  return (
                    <button
                      type="button"
                      onClick={handleResetDefaultView}
                      style={{
                        background: "none",
                        border: "1px dashed #94a3b8",
                        borderRadius: 6,
                        padding: "5px 10px",
                        fontSize: 11,
                        color: "#64748b",
                        cursor: "pointer",
                      }}
                      title="Reset saved default view to All Equipment"
                    >
                      Reset Default
                    </button>
                  );
                }
              } catch {}
              return null;
            })()}

            {isFiltered && (
              <button
                type="button"
                onClick={() => { setSelectedLoads("all"); setSearchQuery(""); }}
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f3f4f6",
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#475569",
                  cursor: "pointer",
                }}
                title="View all equipment"
              >
                Clear
              </button>
            )}

            {isSorted && (
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: 6 }}>
                <span>Sorted by <strong>{sortColumn}</strong> ({sortDirection.toUpperCase()}) · Flat</span>
                <button
                  type="button"
                  onClick={() => setSortColumn(null)}
                  style={{
                    background: "#fff",
                    border: "1px solid #86efac",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#16a34a",
                    cursor: "pointer",
                  }}
                >
                  Return to Hierarchy
                </button>
              </div>
            )}
          </div>

          {/* Gear Icon: Columns Customizer (Symbol only, no text) */}
          <div style={{ position: "relative" }}>
            <button
              type="button"
              onClick={() => setGearOpen((v) => !v)}
              title="Select / Show Table Columns"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: gearOpen ? "#e0f2fe" : "#fff",
                border: `1px solid ${gearOpen ? "#0284c7" : "#cbd5e1"}`,
                borderRadius: 8,
                width: 36,
                height: 36,
                padding: 0,
                color: gearOpen ? "#0284c7" : "#334155",
                cursor: "pointer",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                transform: "none",
                boxSizing: "border-box",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
                e.currentTarget.style.borderColor = "#0f766e";
                e.currentTarget.style.color = "#0f766e";
                e.currentTarget.style.transform = "none";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = gearOpen ? "#e0f2fe" : "#fff";
                e.currentTarget.style.borderColor = gearOpen ? "#0284c7" : "#cbd5e1";
                e.currentTarget.style.color = gearOpen ? "#0284c7" : "#334155";
                e.currentTarget.style.transform = "none";
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
              </svg>
            </button>

            {gearOpen && (
              <>
                <div
                  style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
                  onClick={() => setGearOpen(false)}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    right: 0,
                    background: "#fff",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
                    zIndex: 1000,
                    minWidth: 240,
                    padding: "12px",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #f1f5f9" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>Table Columns</span>
                    <button
                      type="button"
                      onClick={resetColumnsToDefault}
                      style={{ fontSize: 11, color: "#0284c7", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}
                    >
                      Reset
                    </button>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {COLUMN_DEFINITIONS.map((col) => (
                      <label
                        key={col.key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 12.5,
                          color: "#334155",
                          cursor: "pointer",
                          padding: "4px 6px",
                          borderRadius: 4,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                      >
                        <input
                          type="checkbox"
                          checked={!!visibleColumns[col.key]}
                          onChange={() => toggleColumn(col.key)}
                          style={{ cursor: "pointer", accentColor: "#1BA08E" }}
                        />
                        <span>{col.label}</span>
                        {col.isDefault && <span style={{ fontSize: 10, color: "#94a3b8", marginLeft: "auto" }}>Default</span>}
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div style={{ maxHeight: "calc(100vh - 190px)", overflowY: "auto", overflowX: "auto" }}>
          <table style={channelStyles.table}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
              <tr>
                <SortHeader column="name" align="left">
                  <FieldHelp id="live.machineName">Name</FieldHelp>
                </SortHeader>
                {visibleColumns.timestamp && (
                  <th style={channelStyles.th}>
                    <FieldHelp id="live.lastUpdated">Time</FieldHelp>
                  </th>
                )}
                {visibleColumns.power && (
                  <SortHeader column="power">
                    <FieldHelp id="live.power">Power (kW)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.current && (
                  <SortHeader column="current">
                    <FieldHelp id="live.current">Avg Current (A)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.voltage && (
                  <SortHeader column="voltage">
                    <FieldHelp id="live.voltage">Voltage (V)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.powerFactor && (
                  <SortHeader column="powerFactor">
                    <FieldHelp id="live.powerFactor">Power Factor</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.frequency && (
                  <SortHeader column="frequency">
                    <FieldHelp id="live.frequency">Frequency (Hz)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.thdVoltage && (
                  <SortHeader column="thdVoltage">
                    <FieldHelp id="live.thdVoltage">THD V (%)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.thdCurrent && (
                  <SortHeader column="thdCurrent">
                    <FieldHelp id="live.thdCurrent">THD I (%)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.tap && (
                  <SortHeader column="tap">
                    <FieldHelp id="live.tap">TAP (kW)</FieldHelp>
                  </SortHeader>
                )}
                {visibleColumns.status && (
                  <th style={channelStyles.th}>
                    <FieldHelp id="live.onOffStatus">Status</FieldHelp>
                  </th>
                )}
                {visibleColumns.meterStatus && (
                  <th style={channelStyles.th}>
                    <FieldHelp id="live.meterStatus">Meter Status</FieldHelp>
                  </th>
                )}
                {visibleColumns.healthStatus && (
                  <th style={channelStyles.th}>
                    <FieldHelp id="live.healthStatus">Health Status</FieldHelp>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={Object.values(visibleColumns).filter(Boolean).length + 1}
                    style={{
                      padding: "36px 16px",
                      textAlign: "center",
                      color: "#94a3b8",
                      fontSize: 13,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    No equipment selected. Select equipment from the dropdown above.
                  </td>
                </tr>
              ) : (
                sortedData.map((row) => {
                const indentPadding = 12 + row.level * 24;
                const isExpanded = expandedNodes.has(row.id);
                const isVirtual = !!row.isVirtual;
                const levelColors = [
                  "#f0f9ff",
                  "#fefce8",
                  "#f0fdf4",
                  "#fff",
                  "#faf5ff",
                ];
                const rowBackground = levelColors[row.level] || "#fff";

                const rowHoverHandlers = {
                  onMouseEnter: (e) => {
                    e.currentTarget.style.filter = "brightness(0.97)";
                  },
                  onMouseLeave: (e) => {
                    e.currentTarget.style.filter = "none";
                  },
                };

                const expandControl = row.hasChildren ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleNode(row.id);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      fontSize: 12,
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 4,
                      flexShrink: 0,
                    }}
                  >
                    {isExpanded ? "▼" : "▶"}
                  </button>
                ) : (
                  <span style={{ width: 20, display: "inline-block", flexShrink: 0 }} />
                );

                if (isVirtual) {
                  const powerText = formatTableCell(row.power, true);
                  const virtualGapCell = {
                    ...channelStyles.td,
                    ...channelStyles.tdVirtualGap,
                  };
                  return (
                    <tr
                      key={row.id}
                      style={{ ...channelStyles.tr, ...channelStyles.trVirtual }}
                      {...rowHoverHandlers}
                    >
                      <td
                        style={{
                          ...channelStyles.td,
                          ...channelStyles.tdVirtualName,
                          paddingLeft: indentPadding,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {expandControl}
                          <span>{row.name}</span>
                          <VirtualCategoryLabel category={row.category} />
                        </div>
                      </td>
                      {visibleColumns.timestamp && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.power && (
                        <td style={{ ...channelStyles.td, fontWeight: 700 }}>
                          {powerText || null}
                        </td>
                      )}
                      {visibleColumns.current && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.voltage && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.powerFactor && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.frequency && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.thdVoltage && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.thdCurrent && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.tap && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.status && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.meterStatus && <td style={virtualGapCell} aria-hidden="true" />}
                      {visibleColumns.healthStatus && <td style={virtualGapCell} aria-hidden="true" />}
                    </tr>
                  );
                }

                const timeDisplay =
                  !row.timestamp || row.timestamp === "-" || row.meterStatus === "offline"
                    ? "More than 10 mins"
                    : row.timestamp;

                return (
                  <tr
                    key={row.id}
                    style={{
                      ...channelStyles.tr,
                      backgroundColor: rowBackground,
                    }}
                    {...rowHoverHandlers}
                  >
                    <td
                      style={{
                        ...channelStyles.td,
                        textAlign: "left",
                        paddingLeft: indentPadding,
                        fontWeight: row.level < 3 ? 600 : 400,
                        verticalAlign: "middle",
                      }}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 6 }}
                      >
                        {expandControl}
                        <span>{row.name}</span>
                        {row.isMultiParent && (
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            <FieldHelp id="live.multiParent">🔗 Multi-Parent</FieldHelp>
                          </span>
                        )}
                        {row.isSecondaryBranch && (
                          <span
                            style={{
                              padding: "2px 6px",
                              borderRadius: 4,
                              fontSize: 10,
                              fontWeight: 500,
                              background: "#f1f5f9",
                              color: "#64748b",
                            }}
                          >
                            <FieldHelp id="live.sharedBranch">(Shared)</FieldHelp>
                          </span>
                        )}
                      </div>
                    </td>
                    {visibleColumns.timestamp && <td style={channelStyles.td}>{timeDisplay}</td>}
                    {visibleColumns.power && (
                      <td style={{ ...channelStyles.td, fontWeight: 600 }}>
                        {row.power || "-"}
                      </td>
                    )}
                    {visibleColumns.current && (
                      <td style={channelStyles.td}>
                        <AvgCurrentCell row={row} />
                      </td>
                    )}
                    {visibleColumns.voltage && <td style={channelStyles.td}>{row.voltage || "-"}</td>}
                    {visibleColumns.powerFactor && (
                      <td style={channelStyles.td}>
                        {(() => {
                          const pfNum = parseFloat(row.powerFactor);
                          if (!Number.isFinite(pfNum)) return row.powerFactor || "-";
                          let color = "#16a34a"; // green: > 0.8
                          if (pfNum < 0.7) color = "#dc2626"; // red
                          else if (pfNum <= 0.8) color = "#f59e0b"; // orange
                          return (
                            <span style={{ color, fontWeight: 700 }}>
                              {row.powerFactor}
                            </span>
                          );
                        })()}
                      </td>
                    )}
                    {visibleColumns.frequency && <td style={channelStyles.td}>{row.frequency || "-"}</td>}
                    {visibleColumns.thdVoltage && <td style={channelStyles.td}>{row.thdVoltage || "-"}</td>}
                    {visibleColumns.thdCurrent && <td style={channelStyles.td}>{row.thdCurrent || "-"}</td>}
                    {visibleColumns.tap && <td style={channelStyles.td}>{row.tap || "-"}</td>}
                    {visibleColumns.status && (
                      <td style={channelStyles.td}>
                        <StatusBadge value={row.status || "-"} type="status" />
                      </td>
                    )}
                    {visibleColumns.meterStatus && (
                      <td style={channelStyles.td}>
                        <StatusBadge
                          value={row.meterStatus || "-"}
                          type="meterStatus"
                        />
                      </td>
                    )}
                    {visibleColumns.healthStatus && (
                      <td style={channelStyles.td}>
                        <HealthStatusCell
                          row={row}
                          onRefresh={onRefresh}
                          onAssignRatedPower={onAssignRatedPower}
                        />
                      </td>
                    )}
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div style={channelStyles.content}>
      {renderHierarchicalTable()}
    </div>
  );
};

const channelStyles = {
  content: {
    backgroundColor: "#fff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "20px",
    overflow: "visible",
  },
  tableContainer: {
    overflow: "visible",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  },
  th: {
    padding: "12px 8px",
    textAlign: "center",
    borderBottom: "2px solid #dee2e6",
    backgroundColor: "#f8f9fa",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  tr: {
    transition: "background-color 0.2s",
  },
  td: {
    padding: "10px 8px",
    textAlign: "center",
    borderBottom: "1px solid #dee2e6",
  },
  trVirtual: {
    backgroundColor: "#f4f7f6",
  },
  tdVirtualName: {
    textAlign: "left",
    verticalAlign: "middle",
    padding: "11px 8px",
    fontWeight: 700,
    fontSize: 14,
    color: "#134e4a",
    borderBottom: "1px solid #dfe6e4",
    boxShadow: "inset 3px 0 0 #1BA08E",
  },
  tdVirtualGap: {
    padding: "11px 4px",
    borderBottom: "1px solid #dfe6e4",
    backgroundColor: "#f4f7f6",
  },
  machineCell: {
    padding: "10px 8px",
    textAlign: "right",
    borderBottom: "1px solid #dee2e6",
    color: "#6c757d",
    fontWeight: "bold",
  },
  loading: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
  error: {
    textAlign: "center",
    padding: "40px",
    color: "#ef4444",
  },
  noData: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
  },
};

// ========== Main LiveTab Component ==========
const LiveTab = () => {
  const navigate = useNavigate();

  // Health status is derived from live power against the machine's rated power,
  // so a machine with no rated power can never show one. Both the table and the
  // diagram offer a jump straight to that field in Node Settings.
  const goAssignRatedPower = useCallback(
    (target) => {
      navigate(
        buildNodeSettingsLink({
          meterId: target?.meterId || target?.meter_id,
          machineName: target?.name || target?.machine_name,
          focus: FOCUS_RATED_POWER,
        }),
      );
    },
    [navigate],
  );

  const autoRefresh = true;
  const refreshInterval = 15000;
  const [manualRefreshToken, setManualRefreshToken] = useState(0);
  const [activeTab, setActiveTab] = useState(() => {
    const pending = sessionStorage.getItem("enviz-pending-tab");
    if (pending) {
      sessionStorage.removeItem("enviz-pending-tab");
      return pending;
    }
    return "diagram";
  }); // "table", "diagram", "distribution", or "forecast"
  const { selectedBranch, machineToBranchMap, meterIdToBranchMap } = useBranch();

  // Shared data — fetched once, used by both Table and Diagram views
  const [sharedHierarchyTree, setSharedHierarchyTree] = useState(null);
  const [sharedLatestData, setSharedLatestData] = useState(null);
  const [sharedHealthStatus, setSharedHealthStatus] = useState(null);
  const [sharedLoading, setSharedLoading] = useState(true);
  const [distributionMode, setDistributionMode] = useState(false);
  const [lastDataReceivedAt, setLastDataReceivedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Heartbeat to keep live sync status fresh
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Per-endpoint error tracking
  const [hierarchyError, setHierarchyError] = useState(null);
  const [latestError, setLatestError] = useState(null);
  const [healthError, setHealthError] = useState(null);

  const unitPrice = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const price = parseFloat(user?.unit_price);
      return Number.isFinite(price) ? price : 0;
    } catch {
      return 0;
    }
  }, []);

  const username = localStorage.getItem("username");

  const forecastingMachines = useMemo(() => {
    const machines = sharedHierarchyTree ? collectAllMachines(sharedHierarchyTree) : [];

    if (selectedBranch === "All") {
      return machines;
    }

    return machines.filter((machine) => {
      const branchName = getMachineBranch(machine, machineToBranchMap, meterIdToBranchMap);
      return machine.name === selectedBranch || branchName === selectedBranch;
    });
  }, [sharedHierarchyTree, selectedBranch, machineToBranchMap, meterIdToBranchMap]);

  const diagramTree = useMemo(() => {
    if (!sharedHierarchyTree) return null;
    if (selectedBranch === "All") return sharedHierarchyTree;

    return filterTreeByBranch(sharedHierarchyTree, selectedBranch, machineToBranchMap, meterIdToBranchMap) || sharedHierarchyTree;
  }, [sharedHierarchyTree, selectedBranch, machineToBranchMap, meterIdToBranchMap]);

  const fetchSharedData = useCallback(async () => {
    if (!username) {
      const noUserErr = {
        status: 'auth',
        message: 'No username found in session.',
        retryable: false,
        endpoint: 'session',
        severity: 'error'
      };
      setHierarchyError(noUserErr);
      setLatestError(noUserErr);
      setHealthError(noUserErr);
      setSharedLoading(false);
      return;
    }

    // Use Promise.allSettled to fetch all endpoints independently
    const results = await Promise.allSettled([
      api.get(`/hierarchy-tree`, { params: { username } }),
      api.get(`/latest/${username}`),
      api.get(`/status/${username}`),
    ]);


    // ===== Process hierarchy-tree result =====
    if (results[0].status === 'fulfilled') {
      const rawTree = results[0].value.data?.data || results[0].value.data || null;
      if (rawTree) {
        setSharedHierarchyTree((prev) => normalizeTree(rawTree) || prev);
        setHierarchyError(null);
      }
    } else {
      const err = classifyError(results[0].reason, 'hierarchy-tree');
      setHierarchyError(err);
      console.warn("Hierarchy tree fetch failed:", err);
    }

    // ===== Process latest result =====
    if (results[1].status === 'fulfilled') {
      setSharedLatestData((prev) => results[1].value.data || prev);
      setLatestError(null);
      setLastDataReceivedAt(Date.now());
    } else {
      const err = classifyError(results[1].reason, 'latest');
      setLatestError(err);
      console.warn("Latest data fetch failed:", err);
    }

    if (results[0].status === 'fulfilled') {
      setLastDataReceivedAt(Date.now());
    }

    // ===== Process health status result =====
    if (results[2].status === 'fulfilled') {
      const healthData = results[2].value.data || null;
      // Empty {} is valid for health status (means no health data)
      setSharedHealthStatus((prev) => healthData !== null ? healthData : prev);
      setHealthError(null);
    } else {
      const err = classifyError(results[2].reason, 'status');
      // Silent errors (severity='silent') should not set error state
      if (err.severity !== 'silent') {
        setHealthError(err);
      } else {
        setHealthError(null);
      }
      console.warn("Health status fetch failed:", err);
    }

    setSharedLoading(false);
  }, [username]);

  useEffect(() => {
    fetchSharedData();
  }, [fetchSharedData]);
  useEffect(() => {
    if (!autoRefresh || !refreshInterval) return;
    const iv = setInterval(fetchSharedData, refreshInterval);
    return () => clearInterval(iv);
  }, [autoRefresh, refreshInterval, fetchSharedData]);
  useEffect(() => {
    if (manualRefreshToken > 0) fetchSharedData();
  }, [manualRefreshToken, fetchSharedData]);

  // Sync state rule: Stay "Sync" if any data was received under 30 seconds.
  // If no data received for 30s -> "Sync issue"
  const isSynced = lastDataReceivedAt ? (now - lastDataReceivedAt <= 30000) : false;
  const statusText = useMemo(() => {
    if (!lastDataReceivedAt) {
      return sharedLoading ? "Connecting..." : "Sync issue";
    }
    if (now - lastDataReceivedAt <= 30000) {
      return "Sync";
    }
    return "Sync issue";
  }, [now, lastDataReceivedAt, sharedLoading]);

  const handleManualRefresh = () => {
    setManualRefreshToken((token) => token + 1);
  };

  // Listen for refresh event from Header
  useEffect(() => {
    const onHeaderRefresh = () => handleManualRefresh();
    window.addEventListener("enviz-refresh", onHeaderRefresh);
    return () => window.removeEventListener("enviz-refresh", onHeaderRefresh);
  }, []);

  // Listen for tab change event from Header dropdown
  useEffect(() => {
    const onTabChange = (e) => setActiveTab(e.detail);
    window.addEventListener("enviz-tab-change", onTabChange);
    return () => window.removeEventListener("enviz-tab-change", onTabChange);
  }, []);

  // Determine if both critical endpoints failed
  const isCriticalFailure =
    latestError &&
    latestError.severity !== 'silent' &&
    hierarchyError &&
    hierarchyError.severity !== 'silent';

  // Handle 401 redirect
  useEffect(() => {
    if (latestError?.status === 401 || hierarchyError?.status === 401) {
      console.warn("Authentication error detected, redirecting to login");
      setTimeout(() => {
        window.location.href = '/login';
      }, 1000);
    }
  }, [latestError, hierarchyError]);

  const [treeStatusCounts, setTreeStatusCounts] = useState(null);

  const meterIndexMap = useMemo(() => buildMeterIndexMap(diagramTree), [diagramTree]);

  const computedStatusCounts = useMemo(() => {
    const counts = { Normal: 0, Warning: 0, Critical: 0, Unknown: 0, OFF: 0 };
    if (!diagramTree) return counts;

    const readingsMap = new Map();
    if (sharedLatestData?.readings) {
      sharedLatestData.readings.forEach((r) => {
        const mid = r.meter_id;
        if (!mid) return;
        const key = r.meter_index != null ? `${mid}_${r.meter_index}` : mid;
        readingsMap.set(key, r);
      });
    }

    const lookupHealth = (mid, name) => {
      if (!sharedHealthStatus || !mid) return null;
      const idx = meterIndexMap.get(name);
      let raw = null;
      if (idx != null && sharedHealthStatus[`${mid}_${idx}`]) {
        raw = sharedHealthStatus[`${mid}_${idx}`];
      } else if (sharedHealthStatus[mid]) {
        raw = sharedHealthStatus[mid];
      } else {
        const keys = Object.keys(sharedHealthStatus);
        const match = keys.find((k) => k === mid || k.startsWith(`${mid}_`));
        raw = match ? sharedHealthStatus[match] : null;
      }
      if (raw && typeof raw === "object") {
        return { status: raw.status || null, percent: Number(raw.percent) };
      }
      return null;
    };

    const getReading = (node) => {
      if (!node?.meter_id) return null;
      const key = readingKey(node.meter_id, meterIndexMap, node.machine_name);
      return readingsMap.get(key) || null;
    };

    const walk = (node) => {
      const cat = (node.category || "").toUpperCase();
      const isVirtual = !!node.is_virtual || cat === "VIRTUAL";
      if (!isVirtual) {
        const reading = getReading(node);
        const directPower = reading?.power_kw ?? null;
        const power = directPower != null ? directPower : (node.power != null ? node.power : 0);
        const threshold = cat === "GENERATOR" ? 0.5 : 0.05;
        const isOff = Math.abs(power) <= threshold;

        if (isOff) {
          counts.OFF += 1;
        } else {
          const healthInfo = lookupHealth(node.meter_id, node.machine_name);
          const healthStatus = healthInfo?.status || null;
          if (healthStatus === "Normal") counts.Normal += 1;
          else if (healthStatus === "Warning") counts.Warning += 1;
          else if (healthStatus === "Critical") counts.Critical += 1;
          else counts.Unknown += 1;
        }
      }
      (node.children || []).forEach(walk);
    };

    (diagramTree.children || []).forEach(walk);
    return counts;
  }, [diagramTree, sharedLatestData, sharedHealthStatus, meterIndexMap]);

  const activeStatusCounts = treeStatusCounts || computedStatusCounts;
  const statusCountsData = {
    normal: activeStatusCounts.Normal,
    warning: activeStatusCounts.Warning,
    critical: activeStatusCounts.Critical,
    on: activeStatusCounts.Normal + activeStatusCounts.Warning + activeStatusCounts.Critical + activeStatusCounts.Unknown,
    off: activeStatusCounts.OFF,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        minHeight: "calc(100vh - 65px)",
        height: activeTab === "diagram" ? "calc(100vh - 65px)" : "auto",
        position: "relative",
        overflow: activeTab === "diagram" ? "hidden" : "visible",
        backgroundColor: "#f8fafc",
      }}
    >
      {/* Show full error state if both critical endpoints fail */}
      {isCriticalFailure && (
        <FullErrorState
          error={latestError}
          onRetry={handleManualRefresh}
          loading={sharedLoading}
        />
      )}

      {/* If critical endpoints have data, show with optional warning banners */}
      {!isCriticalFailure && (
        <>
          {/* Warning banner for partial failures */}
          {hierarchyError && shouldShowError(hierarchyError) && (
            <div style={{ margin: "8px 14px 0 14px", position: "relative", zIndex: 40 }}>
              <ErrorBanner
                error={hierarchyError}
                onRetry={handleManualRefresh}
                onDismiss={() => setHierarchyError(null)}
              />
            </div>
          )}
          {latestError && shouldShowError(latestError) && (
            <div style={{ margin: "8px 14px 0 14px", position: "relative", zIndex: 40 }}>
              <ErrorBanner
                error={latestError}
                onRetry={handleManualRefresh}
                onDismiss={() => setLatestError(null)}
              />
            </div>
          )}

          {/* Floating Controls Row (No background card, floating cleanly over diagram space) */}
          <div
            style={{
              position: activeTab === "diagram" ? "absolute" : "relative",
              top: activeTab === "diagram" ? 10 : 0,
              left: activeTab === "diagram" ? 14 : 0,
              right: activeTab === "diagram" ? 14 : 0,
              zIndex: 30,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: activeTab === "diagram" ? "0" : "8px 14px 10px 14px",
              background: "transparent",
              border: "none",
              boxShadow: "none",
              margin: activeTab === "diagram" ? "0" : "6px 0 10px 0",
              flexWrap: "wrap",
              pointerEvents: activeTab === "diagram" ? "none" : "auto",
            }}
          >
            {/* View Selector Pill & Sync Badge */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, pointerEvents: "auto" }}>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                  textShadow: activeTab === "diagram" ? "0 1px 2px rgba(255,255,255,0.9)" : "none",
                }}
              >
                View:
              </span>
              <div
                style={{
                  display: "inline-flex",
                  background: "rgba(255, 255, 255, 0.94)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  borderRadius: 10,
                  padding: 3,
                  border: "1px solid rgba(226, 232, 240, 0.9)",
                  boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
                }}
              >
                <button
                  type="button"
                  onClick={() => setActiveTab("diagram")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 14px",
                    borderRadius: 7,
                    border: "none",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: activeTab === "diagram" ? 600 : 500,
                    color: activeTab === "diagram" ? "#ffffff" : "#475569",
                    backgroundColor: activeTab === "diagram" ? "#0f766e" : "transparent",
                    boxShadow: activeTab === "diagram" ? "0 2px 6px rgba(15, 118, 110, 0.25)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="3" width="6" height="4" rx="1" />
                    <rect x="3" y="17" width="6" height="4" rx="1" />
                    <rect x="15" y="17" width="6" height="4" rx="1" />
                    <path d="M12 7v5" />
                    <path d="M6 12h12" />
                    <path d="M6 12v5" />
                    <path d="M18 12v5" />
                  </svg>
                  Hierarchy
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("table")}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "5px 14px",
                    borderRadius: 7,
                    border: "none",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: activeTab === "table" ? 600 : 500,
                    color: activeTab === "table" ? "#ffffff" : "#475569",
                    backgroundColor: activeTab === "table" ? "#0f766e" : "transparent",
                    boxShadow: activeTab === "table" ? "0 2px 6px rgba(15, 118, 110, 0.25)" : "none",
                    transition: "all 0.15s ease",
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M3 9h18" />
                    <path d="M3 15h18" />
                    <path d="M9 3v18" />
                  </svg>
                  Table
                </button>
              </div>

              {/* Real-time sync badge (Stays Sync if data received under 30s) */}
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: isSynced ? "#15803d" : "#dc2626",
                  backgroundColor: isSynced ? "rgba(240, 253, 244, 0.95)" : "rgba(254, 242, 242, 0.95)",
                  border: `1px solid ${isSynced ? "#bbf7d0" : "#fca5a5"}`,
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  padding: "4px 10px",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.05)",
                }}
                title={lastDataReceivedAt ? `Last data received: ${new Date(lastDataReceivedAt).toLocaleTimeString()} (${Math.round((now - lastDataReceivedAt) / 1000)}s ago)` : "Waiting for live data..."}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    backgroundColor: isSynced ? "#16a34a" : "#ef4444",
                    boxShadow: isSynced ? "0 0 6px #22c55e" : "none",
                  }}
                />
                {statusText}
              </span>
            </div>

            {/* Status Counts Chips in same row */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", pointerEvents: "auto" }}>
              {/* Normal */}
              <div
                title="Normal: Operating within safe limits (≤85% of rated power)"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: "rgba(240, 253, 244, 0.94)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #bbf7d0",
                  fontSize: "12px",
                  color: "#166534",
                  fontWeight: 500,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#16a34a" }} />
                <span>Normal</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#15803d" }}>
                  {statusCountsData.normal}
                </span>
              </div>

              {/* Warning */}
              <div
                title="Warning: High load between 85% and 100% of rated power"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: "rgba(255, 251, 235, 0.94)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #fde68a",
                  fontSize: "12px",
                  color: "#92400e",
                  fontWeight: 500,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#d97706" }} />
                <span>Warning</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#b45309" }}>
                  {statusCountsData.warning}
                </span>
              </div>

              {/* Critical */}
              <div
                title="Critical: Overloaded! Exceeding 100% of rated capacity"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: statusCountsData.critical > 0 ? "rgba(254, 242, 242, 0.95)" : "rgba(248, 250, 252, 0.92)",
                  backdropFilter: "blur(10px)",
                  border: `1px solid ${statusCountsData.critical > 0 ? "#fca5a5" : "#e2e8f0"}`,
                  fontSize: "12px",
                  color: statusCountsData.critical > 0 ? "#b91c1c" : "#64748b",
                  fontWeight: statusCountsData.critical > 0 ? 700 : 500,
                  boxShadow: statusCountsData.critical > 0 ? "0 0 0 2px rgba(239, 68, 68, 0.15)" : "0 2px 6px rgba(15, 23, 42, 0.04)",
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: statusCountsData.critical > 0 ? "#ef4444" : "#94a3b8" }} />
                <span>Critical</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: statusCountsData.critical > 0 ? "#dc2626" : "#64748b" }}>
                  {statusCountsData.critical}
                </span>
              </div>

              {/* On */}
              <div
                title="Online: Active equipment consuming/generating power"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: "rgba(240, 249, 255, 0.94)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #bae6fd",
                  fontSize: "12px",
                  color: "#0369a1",
                  fontWeight: 500,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#0ea5e9" }} />
                <span>On</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#0284c7" }}>
                  {statusCountsData.on}
                </span>
              </div>

              {/* Off */}
              <div
                title="Offline: Switched off machines"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "4px 10px",
                  borderRadius: 999,
                  backgroundColor: "rgba(248, 250, 252, 0.92)",
                  backdropFilter: "blur(10px)",
                  border: "1px solid #e2e8f0",
                  fontSize: "12px",
                  color: "#475569",
                  fontWeight: 500,
                  boxShadow: "0 2px 6px rgba(15, 23, 42, 0.04)",
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: "#94a3b8" }} />
                <span>Off</span>
                <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#334155" }}>
                  {statusCountsData.off}
                </span>
              </div>
            </div>
          </div>

          {/* Main content views */}
          {activeTab === "table" ? (
            <div style={{ padding: "0 14px 20px 14px" }}>
              <ChannelCheckerContent
                hierarchyTree={diagramTree}
                latestData={sharedLatestData}
                healthStatusData={sharedHealthStatus}
                loading={sharedLoading}
                error={null}
                username={username}
                unitPrice={unitPrice}
                healthError={healthError}
                onRefresh={fetchSharedData}
                onAssignRatedPower={goAssignRatedPower}
              />
            </div>
          ) : activeTab === "diagram" ? (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: "100%",
                height: "100%",
                zIndex: 1,
              }}
            >
              <HierarchyTreeView
                hierarchyTree={diagramTree}
                latestData={sharedLatestData}
                healthStatusData={sharedHealthStatus}
                loading={sharedLoading}
                error={null}
                username={username}
                unitPrice={unitPrice}
                healthError={healthError}
                onAssignRatedPower={goAssignRatedPower}
                onStatusCountsChange={setTreeStatusCounts}
                distributionMode={distributionMode}
                onToggleDistribution={() => setDistributionMode((prev) => !prev)}
              />
            </div>
          ) : activeTab === "distribution" ? (
            <PowerDistributionFlow
              hierarchyTree={sharedHierarchyTree}
              latestData={sharedLatestData}
              loading={sharedLoading}
              selectedBranch={selectedBranch}
              machineToBranchMap={machineToBranchMap}
              meterIdToBranchMap={meterIdToBranchMap}
            />
          ) : activeTab === "forecast" ? (
            <ForecastingChart
              username={username}
              availableMachines={forecastingMachines}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default LiveTab;
